import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import PageHero from "../components/PageHero";
import BookingDialog from "../components/BookingDialog";
import Reveal from "../components/Reveal";
import { Seo } from "../components/Seo";
import { SocialProof } from "../components/SocialProof";
import { PhotosPending } from "../components/PhotosPending";
import { ReassuranceBadges } from "../components/ReassuranceBadges";
import { FaqSection } from "../components/FaqSection";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  Users, Maximize, BedDouble, Bath, Check, ArrowLeft, Calendar, Clock, Accessibility, ChevronLeft, ChevronRight, X,
} from "lucide-react";

export default function AccommodationDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const prefill = {
    arrivee: params.get("arrivee") || "",
    depart: params.get("depart") || "",
    voyageurs: Number(params.get("voyageurs") || 2),
  };

  useEffect(() => {
    setItem(null); setNotFound(false);
    api.get(`/accommodations/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-24 text-center">
      <Seo title="Hébergement introuvable | La Bégaudière" path={`/hebergement/${slug}`} lang={lang} noindex />
      <h1 className="font-heading text-3xl text-ink-900 mb-4">Hébergement introuvable</h1>
      <Link to="/chambres-hotes" className="text-brand">{t("common.backHome")}</Link>
    </div>
  );
  if (!item) return <div className="pt-40 pb-40 text-center text-ink-400">{t("common.loading")}</div>;

  const rawImages = item.images?.length ? item.images : [item.main_image];
  const images = [item.main_image, ...rawImages.filter((x) => x && x !== item.main_image)]
    .filter(Boolean)
    .map(mediaUrl);
  const openLb = (i) => setLightbox(i);
  const closeLb = () => setLightbox(null);
  const prevImg = (e) => { e?.stopPropagation?.(); setLightbox((x) => (x - 1 + images.length) % images.length); };
  const nextImg = (e) => { e?.stopPropagation?.(); setLightbox((x) => (x + 1) % images.length); };
  const specs = [
    item.capacity && [Users, `${item.capacity} ${t("common.people")}`],
    item.surface && [Maximize, `${item.surface} m²`],
    item.beds && [BedDouble, item.beds],
    item.bathrooms && [Bath, `${item.bathrooms}`],
    item.checkin && [Clock, `${item.checkin}`],
    item.accessibility && [Accessibility, item.accessibility],
  ].filter(Boolean);

  const desc = (item.description || "").replace(/\s+/g, " ").trim().slice(0, 160)
    || `${item.name} · ${item.type === "gite" ? "gîte" : "chambre d'hôtes"} au Domaine de la Bégaudière, près du Mont-Saint-Michel.`;
  const accSchema = {
    "@context": "https://schema.org",
    "@type": item.type === "gite" ? "House" : "BedAndBreakfast",
    name: item.name,
    description: desc,
    image: mediaUrl(item.main_image),
    address: {
      "@type": "PostalAddress",
      streetAddress: "12 La Bégaudière", postalCode: "35120",
      addressLocality: "Mont-Dol", addressRegion: "Bretagne", addressCountry: "FR",
    },
    ...(item.capacity ? { occupancy: { "@type": "QuantitativeValue", maxValue: item.capacity } } : {}),
    ...(item.price_from ? {
      offers: { "@type": "Offer", price: item.price_from, priceCurrency: "EUR", availability: "https://schema.org/InStock" },
    } : {}),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://labegaudiere.fr/" },
      { "@type": "ListItem", position: 2, name: item.type === "gite" ? "Gîtes" : "Chambres d'hôtes", item: `https://labegaudiere.fr/${item.type === "gite" ? "gites" : "chambres-hotes"}` },
      { "@type": "ListItem", position: 3, name: item.name, item: `https://labegaudiere.fr/hebergement/${slug}` },
    ],
  };

  return (
    <div>
      <Seo title={`${item.name} | Domaine de la Bégaudière`} description={desc}
        path={`/hebergement/${slug}`} lang={lang} image={mediaUrl(item.main_image)} jsonLd={[accSchema, breadcrumbSchema]} />
      <PageHero
        title={item.name}
        subtitle={item.type === "gite" ? t("common.gite") : t("common.chambre")}
        image={mediaUrl(item.main_image)}
        crumb={item.name}
        photosPending={!!item.photos_pending}
      />

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link to={item.type === "gite" ? "/gites" : "/chambres-hotes"} className="inline-flex items-center gap-2 text-ink-600 hover:text-brand mb-8 text-sm">
            <ArrowLeft size={16} /> {item.type === "gite" ? t("nav.gites") : t("nav.chambres")}
          </Link>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              {/* Gallery */}
              {item.photos_pending ? (
                <div className="mb-10">
                  <PhotosPending className="w-full rounded-2xl aspect-[16/9]" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 mb-10">
                  {images.slice(0, 4).map((img, i) => {
                    const more = i === 3 && images.length > 4;
                    return (
                      <button key={i} onClick={() => openLb(i)} className={`relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`} data-testid={`detail-gallery-${i}`}>
                        <img src={img} alt={`${item.name} — Domaine de la Bégaudière, photo ${i + 1}`} loading="lazy" className="img-zoom w-full h-full object-cover" />
                        {more && (
                          <span className="absolute inset-0 bg-black/50 text-white flex items-center justify-center font-heading text-xl" data-testid="detail-gallery-more">
                            +{images.length - 4}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>)}

              <Reveal>
                <p className="text-ink-600 font-body text-lg leading-relaxed mb-8">{item.description}</p>
              </Reveal>

              {specs.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
                  {specs.map(([Icon, label], i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-white border border-black/5 px-4 py-3.5">
                      <Icon size={20} strokeWidth={1.5} className="text-brand shrink-0" />
                      <span className="text-sm text-ink-700 font-body">{label}</span>
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(item.amenities) && item.amenities.length > 0 && (
                <div className="mb-10">
                  <h2 className="font-heading text-2xl text-ink-900 mb-4">Équipements</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4">
                    {item.amenities.map((a) => (
                      <div key={a} className="flex items-center gap-2.5 text-sm text-ink-700"><Check size={16} className="text-brand" />{a}</div>
                    ))}
                  </div>
                </div>
              )}

              {(item.included || item.not_included) && (
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {item.included && <div className="rounded-2xl bg-brand-50 p-6"><h3 className="font-heading text-lg text-ink-900 mb-2">Inclus</h3><p className="text-sm text-ink-600">{item.included}</p></div>}
                  {item.not_included && <div className="rounded-2xl bg-cream border border-black/5 p-6"><h3 className="font-heading text-lg text-ink-900 mb-2">Non inclus</h3><p className="text-sm text-ink-600">{item.not_included}</p></div>}
                </div>
              )}

              {item.cancellation && <p className="text-sm text-ink-400">{item.cancellation}</p>}
            </div>

            {/* Booking card */}
            <div>
              <div className="lg:sticky lg:top-24 rounded-2xl bg-white border border-black/5 shadow-[0_16px_48px_rgba(44,48,48,0.1)] p-7">
                {item.price_from ? (
                  <p className="text-ink-400 text-sm mb-1">{t("common.from")}</p>
                ) : null}
                {item.price_from ? (
                  <p className="font-heading text-4xl text-ink-900 mb-1">{item.price_from} €</p>
                ) : (
                  <p className="font-heading text-2xl text-ink-900 mb-1">Sur demande</p>
                )}
                {item.price_from ? <p className="text-ink-400 text-sm mb-5">{t("common.perNight")}</p> : <p className="text-ink-400 text-sm mb-5">Tarifs et disponibilités</p>}
                <div className="flex items-center gap-2 text-sm text-ink-600 mb-5">
                  <Calendar size={17} className="text-brand" /> {lang === "en" ? "Real-time availability & pricing" : "Disponibilités et tarifs en temps réel"}
                </div>
                <button onClick={() => setBooking(true)} data-testid="detail-book-btn" className="w-full rounded-full bg-brand text-white py-3.5 font-medium hover:bg-brand-600 transition-colors mb-3">
                  {t("common.book")}
                </button>
                <Link to="/contact" className="block text-center text-sm text-ink-600 hover:text-brand">{t("common.contactDomain")}</Link>
                <ReassuranceBadges className="mt-5 pt-5 border-t border-black/5" />
                {item.caution ? (
                  <p className="mt-4 pt-4 border-t border-black/5 text-center text-xs text-ink-400" data-testid="detail-caution">
                    {t("common.deposit")} : {item.caution} € {t("common.depositNote")}
                  </p>
                ) : null}
              </div>
              <div className="mt-4"><SocialProof compact /></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-t border-black/5" data-testid="internal-links">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl text-ink-900 mb-6">{lang === "en" ? "Discover also" : "À découvrir aussi"}</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/la-region" className="rounded-full border border-black/10 px-5 py-2.5 text-sm text-ink-700 hover:border-brand hover:text-brand transition-colors" data-testid="link-region">{lang === "en" ? "Around Mont-Saint-Michel & Saint-Malo" : "Autour du Mont-Saint-Michel & Saint-Malo"}</Link>
            <Link to={item.type === "gite" ? "/chambres-hotes" : "/gites"} className="rounded-full border border-black/10 px-5 py-2.5 text-sm text-ink-700 hover:border-brand hover:text-brand transition-colors" data-testid="link-other-category">{item.type === "gite" ? t("nav.chambres") : t("nav.gites")}</Link>
            <Link to="/nos-sejours" className="rounded-full border border-black/10 px-5 py-2.5 text-sm text-ink-700 hover:border-brand hover:text-brand transition-colors" data-testid="link-sejours">{t("nav.sejours")}</Link>
          </div>
        </div>
      </section>

      <FaqSection title={t("infosPage.faqTitle")} orders={[1, 2, 4, 5, 6, 8, 9, 12]} className="bg-beige/50" />

      <BookingDialog item={item} prefill={prefill} open={booking} onOpenChange={setBooking} />

      <Dialog open={lightbox !== null} onOpenChange={(v) => !v && closeLb()}>
        <DialogContent
          onKeyDown={(e) => { if (e.key === "ArrowRight") nextImg(); else if (e.key === "ArrowLeft") prevImg(); }}
          className="max-w-6xl bg-transparent border-0 p-0 shadow-none [&>button]:hidden flex items-center justify-center"
          data-testid="lightbox"
        >
          {lightbox !== null && (
            <div className="relative inline-block">
              <img src={images[lightbox]} alt={`${item.name} — photo ${lightbox + 1}`} decoding="async" className="max-h-[85vh] w-auto rounded-2xl object-contain" data-testid="lightbox-image" />
              {images.length > 1 && (
                <>
                  <button onClick={prevImg} aria-label="Photo précédente" data-testid="lightbox-prev" className="absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/90 text-ink-900 flex items-center justify-center shadow-lg hover:bg-white transition-colors">
                    <ChevronLeft size={22} />
                  </button>
                  <button onClick={nextImg} aria-label="Photo suivante" data-testid="lightbox-next" className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/90 text-ink-900 flex items-center justify-center shadow-lg hover:bg-white transition-colors">
                    <ChevronRight size={22} />
                  </button>
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 text-white text-xs px-3 py-1" data-testid="lightbox-counter">{lightbox + 1} / {images.length}</span>
                </>
              )}
              <button onClick={closeLb} aria-label="Fermer" data-testid="lightbox-close" className="absolute right-2 top-2 h-10 w-10 rounded-full bg-white/90 text-ink-900 flex items-center justify-center shadow-lg hover:bg-white">
                <X size={20} />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
